class Modal {
    /**
     * @type {HTMLElement}
     */
    element;

    /**
     * @type {HTMLElement}
     */
    backdrop;

    /**
     * @type {boolean}
     */
    isShown;

    /**
     * @type {boolean}
     */
    isTransitionOngoing;

    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
        this.element = element;
        this.backdrop = document.createElement('DIV');
        this.backdrop.classList.add('modal-backdrop');
        this.backdrop.classList.add('show');

        this.isShown = false;
        this.isTransitionOngoing = false;
    }

    /**
     * Handle the interaction to show the modal.
     *
     * @returns {void}
     */
    show() {
        // The modal is currently visible or the transition is still ongoing or the event was prevented.
        if (this.isShown || this.isTransitionOngoing || this.triggerEvent('show.bs.modal').defaultPrevented) {
            return;
        }

        // Update the flags
        this.isShown = true;
        this.isTransitionOngoing = true;

        // Handle the DOM interactions.
        document.body.classList.add('modal-open');
        this._adjustDialog();
        document.body.append(this.backdrop);
        this._showElement();
    }

    /**
     * Handle the interaction to hide the modal.
     *
     * @returns {void}
     */
    hide() {
        // The modal is currently not visible or the transition is still ongoing or the event was prevented.
        if (!this.isShown || this.isTransitionOngoing || this.triggerEvent('hide.bs.modal').defaultPrevented) {
            return;
        }

        // Update the flags
        this.isShown = false;
        this.isTransitionOngoing = true;

        // Handle the DOM interactions.
        this.element.classList.remove('show');
        this._hideModal();
    }

    /**
     * Perform the given action name.
     *
     * @param {string|undefined} action
     *
     * @returns {Modal}
     */
    handleAction(action) {
        switch (action) {
            case 'show':
                this.show();

                break;

            case 'hide':
                this.hide();

                break;
        }

        return this;
    }

    /**
     * @param {string|Event} event
     *
     * @returns {Event}
     */
    triggerEvent(event) {
        event = event instanceof Event ? event : new CustomEvent(event);
        event.target = this.element;
        this.element.dispatchEvent(event);

        return event;
    }

    /**
     * @private
     */
    _adjustDialog() {
        const isModalOverflowing = this.element.scrollHeight > document.documentElement.clientHeight;
        const scrollbarWidth = 0;
        const isBodyOverflowing = scrollbarWidth > 0;

        if (isBodyOverflowing && !isModalOverflowing) {
            const property = this.isRTL() ? 'paddingLeft' : 'paddingRight';
            this.element.style[property] = `${scrollbarWidth}px`;
        }

        if (!isBodyOverflowing && isModalOverflowing) {
            const property = this.isRTL() ? 'paddingRight' : 'paddingLeft';
            this.element.style[property] = `${scrollbarWidth}px`;
        }
    }

    /**
     * @private
     */
    _showElement() {
        // try to append dynamic modal
        if (!document.body.contains(this.element)) {
            document.body.append(this.element);
        }

        this.element.style.display = 'block';
        this.element.removeAttribute('aria-hidden');
        this.element.setAttribute('aria-modal', 'true');
        this.element.setAttribute('role', 'dialog');
        this.element.scrollTop = 0;

        const modalBody = this.element.querySelector('.modal-body');

        if (modalBody) {
            modalBody.scrollTop = 0;
        }

        this.element.classList.add('show');

        setTimeout(() => {
            this.isTransitionOngoing = false;

            this.triggerEvent('shown.bs.modal');
        }, 300);
    }

    /**
     * @private
     */
    _hideModal() {
        this.element.style.display = 'none';
        this.element.setAttribute('aria-hidden', 'true');
        this.element.removeAttribute('aria-modal');
        this.element.removeAttribute('role');
        this.isTransitionOngoing = false;

        this.backdrop.remove();
        document.body.classList.remove('modal-open');
        this.triggerEvent('hidden.bs.modal');
    }

    /**
     * @returns {boolean}
     */
    isRTL() {
        return true;
    }
}

/**
 * @param {string|undefined} action
 *
 * @returns {Modal}
 */
HTMLElement.prototype.modal = function (action) {
    // In the case the element does not have a modal instance yet.
    if (typeof this._modal === 'undefined') {
        this._modal = new Modal(this);
    }

    return this._modal.handleAction(action);
};
